import type { CSSProperties, FunctionComponent } from 'react';
import React from 'react';
import cx from 'classnames';

import { makeRGBA, TrelloBlue500, useLightText } from '@trello/colors';
import { TextDefaultColor, TextLightColor } from '@trello/nachos/tokens';

import styles from './Board.less';

export interface BoardProps {
  bgColor?: string;
  bgImage?: string | null;
  borderColor?: string;
  className?: string;
  hasError?: boolean;
  headerBgColor?: string | null;
  loading?: boolean;
  numLists?: number;
  title?: string;
  widthPx?: number;
  style?: CSSProperties;
  gradientLocation?: 'top' | 'bottom';
}

export const Board: FunctionComponent<BoardProps> = ({
  bgColor,
  bgImage,
  borderColor,
  hasError,
  headerBgColor,
  children,
  className,
  loading,
  numLists = 5,
  widthPx,
  style = {},
  gradientLocation = 'top',
}) => {
  // Apply base classes
  const boardClasses = cx(
    styles.board,
    className,
    hasError && styles.boardError,
    loading && styles.boardLoading,
  );

  // Set custom background color
  const dynamicStyles: CSSProperties = { ...style };
  if (bgColor) {
    dynamicStyles.backgroundColor = bgColor;
  }

  // Set custom background image
  if (bgImage && !headerBgColor) {
    dynamicStyles.backgroundImage = `url(${bgImage})`;
    dynamicStyles.backgroundSize = 'cover';
    dynamicStyles.backgroundPosition = 'bottom';
  } else if (headerBgColor) {
    const headerColor = makeRGBA(headerBgColor, 0.7);
    const headerBg = `linear-gradient(0deg, ${headerColor} 50%, ${headerColor} 0%)`;
    dynamicStyles.backgroundSize = `100% 64px, cover`;

    if (gradientLocation === 'bottom') {
      dynamicStyles.backgroundPosition = 'bottom';
    }

    dynamicStyles.backgroundImage = `${headerBg}`;

    if (bgImage) {
      dynamicStyles.backgroundImage += `, url(${bgImage})`;
    }
  }

  // Set text color based on background color
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (static color, dynamic background)
  const color = headerBgColor || bgColor || TrelloBlue500;
  dynamicStyles.color =
    useLightText(color) && !hasError ? TextLightColor : TextDefaultColor;

  // Set dynamic width based on number of lists
  if (numLists !== 5) {
    dynamicStyles.width = `${numLists * 48 + 8}px`;
  }

  // Set custom border color
  if (borderColor) {
    dynamicStyles.border = `1px solid ${borderColor}`;
  }

  if (widthPx) {
    dynamicStyles.width = `${widthPx}px`;
  }

  return (
    <div className={boardClasses} style={dynamicStyles}>
      {children}
    </div>
  );
};

export const BoardLists: FunctionComponent = ({ children }) => (
  <div className={styles.boardLists}>{children}</div>
);

export const TeamName: FunctionComponent = ({ children }) => (
  <h2 className={styles.teamName}>{children}</h2>
);

export const BoardName: FunctionComponent<{ className?: string }> = ({
  children,
  className,
}) => <h1 className={cx(styles.boardName, className)}>{children}</h1>;

export const ErrorMessage: FunctionComponent = ({ children }) => (
  <h1 className={cx(styles.boardName, styles.errorMessage)}>{children}</h1>
);
